import * as React from "react";

import { useButton_unstable } from "@fluentui/react-components";

import { useMergedTooltipAndKeytipRef } from "@1js/acui-hooks";

import { deriveButtonKeytip } from "@1js/acui-keytip";
import { useAppButtonState } from "./useAppButtonState";
import type { AppButtonProps, AppButtonState } from "./AppButton.types";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";

/**
 * Given user props, returns state for an `AppButton`.
 */
export const useAppButton = (
  props: AppButtonProps,
  ref: React.Ref<HTMLButtonElement>
): AppButtonState => {
  const { disabled, customKeytipFunction, icon, hoverIcon } = props;

  // Track hover state for icon swapping
  const [isHovered, setIsHovered] = React.useState(false);
  const appContext = React.useContext(AppContext);
  const onHoverIconEnabled = checkExperiment(
    appContext,
    ribbonExperiments.onHoverIconEnabled
  );
  const currentIcon =
    !!onHoverIconEnabled && isHovered && hoverIcon ? hoverIcon : icon;

  const mergedRefWithKeytip = useMergedTooltipAndKeytipRef(
    {
      ...props,
      deriveKeytip: customKeytipFunction ?? deriveButtonKeytip,
    },
    ref,
    "AppButton"
  );

  // 'type' override here needed so that our types (e.g. "AppButtonProps") aren't assigned to the HTML element
  // TODO: should disabledFocusable be the same as disabled? aria-disabled requires disabledFocusable to be set here
  const buttonState = useButton_unstable(
    {
      ...props,
      icon: currentIcon,
      type: "button",
      disabledFocusable: disabled,
    },
    mergedRefWithKeytip
  );

  return useAppButtonState(props, buttonState, setIsHovered);
};
